import { Crypto } from "./crypto";

export enum DexPriceType {
  BUY = "BUY",
  SELL = "SELL",
}

export interface CryptoExchangeInfo {
  quoteCoin: string;
  canDeposit?: boolean;
  canWithdrawal?: boolean;
  exchangeId: Exchange["id"];
  exchangeName: Exchange["name"];
  price: number;
  formattedPrice: string;
  formattedPriceInQuoteCoin: string;
  volume: number;
  formattedVolume: string;
  isMarkedAsRead: boolean;
  address?: string;
  type?: DexPriceType;
  originalSymbol?: Crypto["symbol"];
}

export enum InfoValuesType {
  MIN = "minValueExchanges",
  MAX = "maxValueExchanges",
}

export interface CryptoArbitrageValue {
  infosByExchanges: CryptoExchangeInfo[];
  maxDifference: number;
  [InfoValuesType.MIN]: CryptoExchangeInfo[];
  [InfoValuesType.MAX]: CryptoExchangeInfo[];
  id: string;
  name: string;
  symbol: string;
  slug: string;
}

export enum Filter {
  ALL,
  FUTURES_ONLY,
}

export interface PaginatedMeta {
  page: number;
  limit: number;
  itemCount: number;
  pageCount: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

export interface Paginated<T> {
  data: T;
  meta: PaginatedMeta;
}
export type PaginatedWithAlert<T> = Paginated<T> & {
  alert: Notification;
};

export interface Exchange {
  id: string;
  name: string;
}

type Top10CryptosBySymbol = Record<
  Crypto["symbol"],
  CryptoArbitrageValue | ArbitrageOrderBooks
>;

export interface Top10Prices {
  top10CryptosBySymbol: Top10CryptosBySymbol;
  top10Cryptos: Crypto["slug"][];
}

export interface ArbitrageOrderBooksItem {
  exchangeId: string;
  symbol: string;
  symbolOnExchange: string;
  address: string;
  quoteCoin: string;
  networks: any[];
  buyEffectivePrice: number;
  coinsToBuy: number;
  sellEffectivePrice: number;
  finalUsdAmount: number;
  topAsk: number;
  topBid: number;
  orderbook: Tips;
  isMarkedAsRead: boolean;
  hasActiveAlert: boolean;
  formattedBuyEffectivePrice: string;
  formattedCoinsToBuy: string;
  formattedCoinsToSell: string;
  exchangeName: string;
  formattedSellEffectivePrice: string;
  lastOrderbookUpdate: number;
}

export interface ArbitrageOrderBooks {
  id: string;
  symbol: string;
  name: string;
  slug: string;
  maxProfit: number;
  startUsdAmount: number;
  profitPercentage: number;
  calculatedAt: number;
  bookByExchange: ArbitrageOrderBooksItem[];
  [InfoValuesType.MAX]: ArbitrageOrderBooksItem[];
  [InfoValuesType.MIN]: ArbitrageOrderBooksItem[];
}

export type ProfitsLocalCache = Map<
  ArbitrageOrderBooks["id"],
  ArbitrageOrderBooks
>;

export type NotificationsLocalCache = Map<Notification["id"], Notification>;

// WebSocket Profits Actions
export type SnapshotResponse<T> = {
  action: "SNAPSHOT";
  data: T[];
};

export type UpdateResponse<T> = {
  action: "UPDATE";
  id: string;
  data: T;
};

export type DeleteResponse<T> = {
  action: "DELETE";
  id: T;
};

export type WSProfitsResponse =
  | SnapshotResponse<ArbitrageOrderBooks>
  | UpdateResponse<ArbitrageOrderBooks>
  | DeleteResponse<string>;

export type WSNotificationsResponse =
  | SnapshotResponse<Notification>
  | UpdateResponse<Notification>
  | DeleteResponse<string>;

export type Notification = {
  id: string;
  crypto: Pick<Crypto, "id" | "name" | "symbol" | "slug">;
  minExchange: string;
  maxExchange: string;
  profit: number;
  profitPercentage: number;
  type: NotificationConfig;
};

export type NotificationConfig = {
  type: NotificationType;
  milliseconds: number;
};

export enum NotificationType {
  CRITICAL = "CRITICAL",
  HIGH = "HIGH",
  IMPORTANT = "IMPORTANT",
  NONE = "NONE",
}

export interface ArbitrageCryptoOrderBooks {
  id: string;
  symbol: string;
  name: string;
  slug: string;
  maxProfit: number;
  profitPercentage: number;
  startUsdAmount: number;
  bookByExchange: BookByExchange[];
}

//Legacy
export interface BookByExchange {
  exchangeId: string;
  symbol: string;
  symbolOnExchange: string;
  address: string;
  quoteCoin: string;
  networks: [];
  buyEffectivePrice: number | null;
  coinsToBuy: number;
  sellEffectivePrice: number;
  finalUsdAmount: number;
  topAsk: number | null;
  topBid: number | null;
  orderbook: null;
  isMarkedAsRead: boolean;
  hasActiveAlert: boolean;
  exchangeName: string;
  formattedBuyEffectivePrice?: string;
  formattedCoinsToBuy: string;
  formattedSellEffectivePrice: string;
  lastOrderbookUpdate?: number;
  bids?: Array<{ price: string; quantity: string }>;
  asks?: Array<{ price: string; quantity: string }>;
}

export interface Network {
  networkId: string;
  networkOnExchange: string;
  depositEnabled: boolean;
  withdrawEnabled: boolean;
}

export interface Tips {
  ask: Tip;
  bid: Tip;
}

export interface Tip {
  price: number;
  totalAmount: number;
}
